"use client";

import { useScrolledPast } from "@/hooks/useScrolledPast";
import { useInView } from "@/hooks/useInView";
import { MEET_CAL_LINK, MEET_CAL_NAMESPACE } from "@/lib/config";
import { MEET_BENEFITS, MEET_CALENDAR, MEET_CALLBACK, MEET_HERO } from "@/lib/meet";
import type { Funnel as FunnelState } from "@/hooks/useFunnel";
import { cn } from "@/lib/utils";
import { CalInline } from "./CalInline";
import { Icon } from "./Icon";

// ============================================================================
// /meet — point expert offert. Présentation seule : le tracking, la popup de
// sortie et la réservation sont orchestrés par useFunnel (via `f`).
//   Promesse → bénéfices → calendrier cal.com intégré. Le CTA ne fait que
//   descendre au calendrier : une seule action possible sur la page.
// ============================================================================

const CAL_ANCHOR = "creneau";
const CAL_METADATA = { funnel: "meet" };

export function MeetLanding({ f }: { f: FunnelState }) {
  const scrolled = useScrolledPast(480);
  const [calRef, calVisible] = useInView<HTMLElement>();

  const goToCalendar = (location: string) => {
    f.trackCta(location);
    document.getElementById(CAL_ANCHOR)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <main className="w-full pb-28 sm:pb-0">
      {/* ---- Bandeau ----------------------------------------------------- */}
      <div className="w-full border-b border-ink/10 bg-cream/95 backdrop-blur">
        <div className="mx-auto flex max-w-2xl items-center justify-between gap-3 px-5 py-3">
          <p className="text-sm font-semibold text-brand">{MEET_HERO.badge}</p>
          <button
            type="button"
            onClick={() => goToCalendar("topbar")}
            className="cursor-pointer rounded-lg bg-gold px-4 py-2 text-sm font-semibold text-ink transition hover:bg-gold-dark"
          >
            Réserver
          </button>
        </div>
      </div>

      {/* ---- Hero -------------------------------------------------------- */}
      <section className="mx-auto w-full max-w-2xl px-5 pt-8 pb-10 text-center sm:pt-14">
        <h1 className="text-balance text-[30px] font-extrabold leading-[1.1] tracking-tight sm:text-[46px]">
          {MEET_HERO.headline}
        </h1>
        <p className="mx-auto mt-4 max-w-xl text-pretty text-[15px] leading-relaxed text-ink/70 sm:text-lg">
          {MEET_HERO.promise}
        </p>
        <p className="mx-auto mt-4 max-w-md text-pretty text-[13px] font-medium leading-snug text-ink/80 sm:text-sm">
          <Icon name="rise" className="mr-1.5 inline-block h-4 w-4 align-[-3px] text-brand" />
          {MEET_HERO.proof}
        </p>
        <button
          type="button"
          onClick={() => goToCalendar("hero")}
          className="mt-8 inline-flex w-full cursor-pointer items-center justify-center rounded-xl bg-gold px-7 py-4 text-base font-semibold text-ink shadow-lg shadow-gold/25 transition hover:bg-gold-dark active:scale-[0.99] sm:w-auto sm:hover:scale-[1.02]"
        >
          {MEET_HERO.cta} →
        </button>
        <p className="mt-4 text-xs text-ink/50">{MEET_HERO.hint}</p>
      </section>

      {/* ---- Ce que vous obtenez ------------------------------------------ */}
      <section className="w-full bg-cream-dark/40">
        <div className="mx-auto grid w-full max-w-2xl gap-4 px-5 py-12 sm:grid-cols-3 sm:gap-5">
          {MEET_BENEFITS.map((b) => (
            <div key={b.title} className="rounded-2xl border border-ink/10 bg-white p-5">
              <Icon name={b.icon} className="h-8 w-8 text-brand" />
              <p className="mt-3 font-semibold text-ink">{b.title}</p>
              <p className="mt-1.5 text-sm leading-relaxed text-ink/65">{b.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ---- Calendrier ---------------------------------------------------- */}
      <section
        id={CAL_ANCHOR}
        ref={calRef}
        className="mx-auto w-full max-w-3xl scroll-mt-4 px-3 pt-12 pb-14 sm:px-5"
      >
        <div className="px-2 text-center">
          <h2 className="text-balance text-[26px] font-extrabold leading-tight tracking-tight sm:text-[32px]">
            {MEET_CALENDAR.title}
          </h2>
          <p className="mx-auto mt-2 max-w-md text-sm text-ink/65">{MEET_CALENDAR.subtitle}</p>
        </div>
        <div className="mt-6 min-h-[640px] overflow-hidden rounded-2xl border border-ink/10 bg-white shadow-xl shadow-ink/5">
          <CalInline
            namespace={MEET_CAL_NAMESPACE}
            calLink={MEET_CAL_LINK}
            onBooked={f.onMeetBooked}
            prefill={f.meetPrefill}
            utms={f.utms as Record<string, string>}
            metadata={CAL_METADATA}
          />
        </div>
      </section>

      {/* ---- Barre de réservation (mobile) --------------------------------- */}
      <div
        className={cn(
          "fixed inset-x-0 bottom-0 z-40 border-t border-ink/10 bg-cream/95 px-4 pt-3.5 pb-4 backdrop-blur transition-transform duration-200 sm:hidden",
          scrolled && !calVisible ? "translate-y-0" : "translate-y-full"
        )}
      >
        <button
          type="button"
          onClick={() => goToCalendar("sticky_mobile")}
          className="flex w-full cursor-pointer items-center justify-center rounded-xl bg-gold px-6 py-3.5 text-base font-semibold text-ink shadow-lg shadow-gold/25 transition active:scale-[0.99]"
        >
          {MEET_HERO.cta}
        </button>
        <p className="mt-2 text-center text-[11px] text-ink/50">{MEET_HERO.hint}</p>
      </div>

      {/* ---- Après la popup de sortie ------------------------------------- */}
      {f.meetCallback && (
        <div
          onClick={f.dismissMeetCallback}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-2xl bg-white p-6 text-center shadow-2xl"
          >
            <p className="text-lg font-extrabold text-ink">{MEET_CALLBACK.title}</p>
            <p className="mt-2 text-sm text-ink/70">{MEET_CALLBACK.body}</p>
            {f.meetCallback === "qualified" && (
              <>
                <p className="mt-4 text-sm text-ink/70">{MEET_CALLBACK.qualifiedExtra}</p>
                <button
                  type="button"
                  onClick={() => {
                    f.dismissMeetCallback();
                    goToCalendar("callback_modal");
                  }}
                  className="mt-5 inline-flex w-full cursor-pointer items-center justify-center rounded-xl bg-gold px-6 py-3.5 font-semibold text-ink transition hover:bg-gold-dark"
                >
                  {MEET_CALLBACK.cta}
                </button>
              </>
            )}
            {f.meetCallback === "disqualified" && (
              <button
                type="button"
                onClick={f.dismissMeetCallback}
                className="mt-5 cursor-pointer text-sm font-medium text-brand underline-offset-2 hover:underline"
              >
                Fermer
              </button>
            )}
          </div>
        </div>
      )}
    </main>
  );
}
